import {
  promotionalOfferCrudConfig,
  type PromotionalOffer,
} from '@/config/objects/promotional-offers';
import { CrudListView, type CrudConfig } from '@startupquickstart/frontend';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface PromotionalOffersListProps {
  /**
   * Optional custom config (with modified endpoints, actions, etc.)
   */
  config?: CrudConfig<PromotionalOffer>;
  /**
   * Optional default where clause for filtering
   */
  defaultWhere?: Record<string, unknown>;
  /**
   * Optional custom onItemClick handler
   */
  onItemClick?: (promotionalOffer: PromotionalOffer) => void;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Base URL for navigation, defaults to /promotional-offers
   */
  baseUrl?: string;
  /**
   * Breadcrumbs for navigation
   */
  breadcrumbs?: Array<{ label: string; href?: string; current?: boolean }>;
}

/**
 * Reusable promotional offers list component
 * Pass a custom config with modified actions to customize behavior
 */
export function PromotionalOffersList({
  config,
  baseUrl = '/promotional-offers',
  defaultWhere,
  onItemClick,
  className,
  breadcrumbs,
}: PromotionalOffersListProps) {
  const navigate = useNavigate();

  const _onItemClick = useCallback(
    (promotionalOffer: PromotionalOffer) => {
      if (onItemClick) {
        onItemClick(promotionalOffer);
      } else {
        navigate(`/promotional-offers/${promotionalOffer.id}`);
      }
    },
    [navigate, onItemClick],
  );

  return (
    <div className={className}>
      <CrudListView
        config={config || promotionalOfferCrudConfig}
        baseUrl={baseUrl}
        onItemClick={_onItemClick}
        defaultWhere={defaultWhere}
        breadcrumbs={breadcrumbs}
        getValueFromItem={(promotionalOffer: PromotionalOffer) => promotionalOffer.id}
        getLabelFromItem={(promotionalOffer: PromotionalOffer) =>
          promotionalOffer.name || promotionalOffer.title || 'Unnamed Offer'
        }
      />
    </div>
  );
}
